import {createContext, ReactNode, useCallback, useContext, useEffect, useState} from "react";

import {api} from '../services/Api';
import {useHistory} from "react-router-dom";
import {useAuth} from "./AuthenticationContext";
import {useAlert} from "./AlertContext";

type Connections = [{
	cto: number;
	download: string;
	id: number;
	pop: number;
	status: string;
	tipo_conexao: string;
	upload: string;
}]

type OpenInvoice = {
	data_vencimento_mais_antiga: string;
	quantidade_em_aberto: number;
	alertContracts: boolean;
	status_mais_antiga: string;
	valor_mais_antiga: number;
}

type StreamingType = [{
	id_contrato_servico: number | null;
	id_contrato_servico_produto: number | null;
	nome: string;
	servicos: [];
}]

type Contract = {
	bairro: string;
	cidade: string;
	conexoes: Connections;
	data: string;
	desbloqueio: string;
	download: string;
	download2: string;
	download3: string;
	download_ativa: string;
	endereco: string;
	endereco_numero: string;
	fatura_aberta: OpenInvoice;
	id: number;
	id_anexo_download_contrato: number;
	ids_anexo_download_contrato: [];
	id_contrato: number;
	id_tipo_conexao: number;
	link_assinatura: string;
	links_assinatura: [];
	numero_contrato: string;
	pendente_de_assinatura: boolean;
	plano: string;
	preco: string;
	status: string;
	upload: string;
	upload2: string;
	upload3: string;
	upload_ativa: string;
	streamings: StreamingType | null;
    playhub: boolean;
}

type UserProps = {
	cdoc: string;
	cid: number;
	cname: string;
	ctopic: [[{
		conexoes: [];
		contratos: [];
		faturas: [];
		geral: [];
	}]];
	database: string;
	exp: number;
	iat: number;
	iss: number;
	uid: number;
	ulogin: string;
	uname: string;
}

type SessionProps = {
	token: string;
	date_access: string;
	data: UserProps;
	exp: number;
	id_contract: string;
	notifications_invoices: boolean,
	notifications_contracts: boolean,
	notifications_connections: boolean,
	numero_contrato: string;
}

type Radios = {
	id_contrato: string;
	status: string;
	plano: string;
	endereco: string;
	endereco_numero: string;
	bairro: string;
	cidade: string;
	preco: string;
	numero_contrato: string;
}

type ContractContextData = {
	handleSetModalContractShow: (state: boolean) => void;
	handleSetContractSelected: (id: string | '', numero_contrato: string | '') => void;
	handleLoad: () => void;
	contracts: Contract[] | null;
	loadingContracts: boolean;
	contractSelected: Contract | null;
	connections: Connections | null;
	openInvoice: OpenInvoice | null;
	loadingModalContract: boolean;
	radios: Radios[] | null;
	showModalContract: boolean;
}

type ContractContextProviderProps = {
	children: ReactNode;
}

export const ContractContext = createContext<ContractContextData>({} as ContractContextData);

export function ContractContextProvider(props: ContractContextProviderProps){
	const history = useHistory();

	const { handleSetAlert } = useAlert();

	const { session, handleSetSession, logOut } = useAuth();

	const [contracts, setContracts] = useState<Contract[] | null>(null);
	const [loadingContracts, setLoadingContracts] = useState(false);
	const [contractSelected, setContractSelected] = useState<Contract | null>(null);
	const [connections, setConnections] = useState<Connections | null>(null);
	const [openInvoice, setOpenInvoice] = useState<OpenInvoice | null>(null);
	const [showModalContract, setShowModalContract] = useState(false);
	const [loadingModalContract, setLoadingModalContract] = useState(false);
	const [radios, setRadios] = useState<Radios[] | null>(null);

	const urlParams = new URLSearchParams(window.location.search);
	const localParam = urlParams.get('local');

	const handleLoad = useCallback(async () => {
		if (session) {
			const headers = {
				'Authorization': 'Beaser ' + session.token,
				'Content-Type': 'application/json',
			};

			return await api.get('v1/pessoas/clientes/contratos', {headers})
				.catch(async (erro) => {
					await logOut();
					history.push('/')
					handleSetAlert({
						visible: true,
						variant: 'danger',
						message: erro.response.data.message,
						timeout: 5000
					});
				});
		}
	},[session, handleSetAlert, history, logOut]);

	const handleSaveContractSelected = useCallback( (id: string | '', local: string | null, numero_contrato: string | '') => {
		if(session){
			let data: SessionProps;
			data = {...session, id_contract: id, numero_contrato: numero_contrato};
			localStorage.setItem('session_netcomet', JSON.stringify(data));
			handleSetSession(data);
			if (local) {
				urlParams.delete(local);
				const newUrl = window.location.pathname;
				window.history.replaceState({}, '', newUrl);
				history.push(local);
			}
		}
	}, [session, handleSetSession, history])


	useEffect(() => {
		if(session && !contracts){
			setLoadingContracts(true);
			handleLoad().then((response: any) => {
				if(response.data.length > 1) {
					let radiosData = [] as Radios[];
					response.data.forEach((element: Contract) => {
						radiosData.push({
							id_contrato: element.id_contrato.toString(),
							status: element.status,
							plano: element.plano,
							endereco: element.endereco,
							endereco_numero: element.endereco_numero,
							bairro: element.bairro,
							cidade: element.cidade,
							preco: element.preco,
							numero_contrato: element.numero_contrato,
						})
						if(element.id_contrato.toString() === session.id_contract.toString()){
							setContractSelected(element);
							setConnections(element.conexoes);
							setOpenInvoice(element.fatura_aberta);
						}
					});
					setContracts(response.data);
					setRadios(radiosData);
					setLoadingContracts(false);
					if(!session.id_contract){
						setShowModalContract(true);
					}
				} else if (response.data.length === 1) {
					setContracts(response.data);
					setConnections(response.data[0].conexoes);
					setOpenInvoice(response.data[0].fatura_aberta);
					handleSaveContractSelected(response.data[0].id_contrato, localParam, response.data[0].numero_contrato);
					setContractSelected(response.data[0]);
					setLoadingContracts(false);
				} else {
					setContracts(null);
					setConnections(null);
					setOpenInvoice(null);
					setContractSelected(null);
					setLoadingContracts(false);
				}
			}).catch(() => {
				setContracts(null);
				setConnections(null);
				setOpenInvoice(null);
				setContractSelected(null);
				setLoadingContracts(false);
			});
		}
	}, [session, contracts, handleLoad, handleSaveContractSelected, localParam])

	useEffect(() => {
		if(showModalContract){
			setShowModalContract(true);
			setLoadingModalContract(true);
			handleLoad().then((response: any) => {
				let radiosData = [] as Radios[];
				response.data.forEach((element: Contract) => {
					radiosData.push({
						id_contrato: element.id_contrato.toString(),
						status: element.status,
						plano: element.plano,
						endereco: element.endereco,
						endereco_numero: element.endereco_numero,
						bairro: element.bairro,
						cidade: element.cidade,
						preco: element.preco,
						numero_contrato: element.numero_contrato,
					})
				});
				setContracts(response.data);
				setRadios(radiosData);
				setLoadingModalContract(false);
			}).catch((error) => {
				setContracts(null);
				setConnections(null);
				setOpenInvoice(null);
				setContractSelected(null);
				setLoadingContracts(false);
			});
		}
	}, [showModalContract, handleLoad])

	function handleSetContractSelected(id: string | '', numero_contrato: string | '') {
		if(session){
			contracts?.forEach((element: Contract) => {
				if(element.id_contrato.toString() === id.toString()){
					setContractSelected(element);
					setConnections(element.conexoes);
					setOpenInvoice(element.fatura_aberta);
				}
			});
			handleSaveContractSelected(id, localParam, numero_contrato);
			setShowModalContract(false);
		}
	}

	function handleSetModalContractShow(state: boolean){
		setShowModalContract(state);
		history.push('/menu');
	}

	const value = {
		contracts,
		loadingContracts,
		contractSelected,
		connections,
		openInvoice,
		loadingModalContract,
		radios,
		showModalContract,
		handleSetModalContractShow,
		handleSetContractSelected,
		handleLoad,
	}

	return(
		<ContractContext.Provider value={value}>
			{props.children}
		</ContractContext.Provider>
	);
}

export function useContract(){
	return useContext(ContractContext);
}
